// ./src/overlayStack.ts — WHICH open drawer / modal is on top, and whose Esc / ⌘S a key press belongs to (suggested task
// "Make create-in-place drawers slide in", 2026-10-03).
//
// ⚠⚠ Mantine closes EVERY open drawer and modal on one Esc: each listens on `window` and none checks whether another one
//   is above it. And every drawer's own shortcuts (apps/app `useModalShortcuts`) listen on `document`. So Esc in a
//   "Create a category" drawer opened from a picker inside an edit drawer closed BOTH, and ⌘S could save both.
//
// ONE STACK, in the order things registered (a later entry is above an earlier one):
//   • "overlay" — AppDrawer, ConfirmModal, AppDrawer's own discard dialog, while open. Mantine's Esc-to-close
//     is on only for the TOP overlay (`useOverlayStack`).
//   • "shortcuts" — a drawer's `useModalShortcuts`, while enabled. It acts only when ITS entry was the LAST entry of the
//     stack when the key went down (`shortcutsMayAct`): nothing — no inner drawer, no confirm modal — opened after it.
//     ⓘ A drawer's own AppDrawer registers BEFORE its shortcuts (a child's effects run before its parent's), so a
//       drawer's shortcuts sit right above its own overlay.
// ⚠⚠ THE STACK IS READ AS IT WAS WHEN THE KEY WENT DOWN — a snapshot taken by a window capture listener added when this
//   module loads (before any drawer has registered its own). Read live, it was wrong for a REAL key press: the browser
//   runs microtasks between listeners, React re-renders there after the inner drawer's Mantine listener closed it, the
//   inner entry is gone by the time the outer drawer's shortcut listener runs — and the outer closed too. A script's
//   `dispatchEvent` has no such checkpoint, which is why the first harness passed over it.
// ⓘ HMR re-runs this module in dev (a second listener after Mantine's) — harmless, a full reload restores the order.
import { useEffect, useRef, useSyncExternalStore } from "react";

type EntryKind = "overlay" | "shortcuts";

interface Entry {
  token: symbol;
  kind: EntryKind;
}

const stack: Entry[] = [];
const listeners = new Set<() => void>();
/** The stack's tokens when the current / last key went down. */
let keydownSnapshot: symbol[] | null = null;

const notify = (): void => {
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

const topOverlay = (): symbol | null => {
  for (let i = stack.length - 1; i >= 0; i -= 1) {
    if (stack[i].kind === "overlay") {
      return stack[i].token;
    }
  }

  return null;
};

const push = (token: symbol, kind: EntryKind): (() => void) => {
  stack.push({ token, kind });
  notify();

  return () => {
    for (let i = stack.length - 1; i >= 0; i -= 1) {
      if (stack[i].token === token) {
        stack.splice(i, 1);
        notify();

        return;
      }
    }
  };
};

/** Put an open drawer / modal on the stack. Returns the function that takes it off again. */
export const pushOverlay = (token: symbol): (() => void) => push(token, "overlay");

/** Put an enabled drawer's shortcuts on the stack. Returns the function that takes them off again. */
export const pushShortcuts = (token: symbol): (() => void) => push(token, "shortcuts");

/** Whether any drawer / modal is open right now. */
export const anyOverlayOpen = (): boolean => topOverlay() !== null;

/**
 * Run `listener` whenever a drawer / modal opens or closes. Returns the unsubscribe. (ROADMAP #131: lists re-read after a
 * timed-out save only once no drawer is open — the drawer is still showing the message about it.)
 */
export const onOverlayStackChange = (listener: () => void): (() => void) => subscribe(listener);

/** Whether `token` is the top open drawer / modal right now (the one Mantine may close on Esc). */
export const isTopOverlay = (token: symbol): boolean => topOverlay() === token;

/**
 * Record the stack as it is now — the snapshot a key press is judged by (exported for the guard). It is dropped once the
 * event has been through every listener, so nothing outside a key press reads a stale one.
 */
export const snapshotForKeydown = (): void => {
  const taken = stack.map((e) => e.token);

  keydownSnapshot = taken;
  setTimeout(() => {
    if (keydownSnapshot === taken) {
      keydownSnapshot = null;
    }
  }, 0);
};

/**
 * Whether a drawer's shortcuts may act on the key being handled: its entry was the LAST one when the key went down.
 * Without a snapshot (no key has gone down — a caller outside a key press) the live stack is used.
 */
export const shortcutsMayAct = (token: symbol): boolean => {
  const tokens = keydownSnapshot ?? stack.map((e) => e.token);

  return tokens.length > 0 && tokens[tokens.length - 1] === token;
};

/**
 * Whether NOTHING — no drawer, modal, palette or drawer shortcuts — was open when the key went down: a PAGE's own
 * shortcuts (⌘S, Esc, Delete, ⌘F, F9…) act only then, never on the page behind an open drawer.
 */
export const nothingOpenAtKeydown = (): boolean => (keydownSnapshot ?? stack.map((e) => e.token)).length === 0;

/**
 * While `opened`, this overlay is on the stack; returns whether it is the TOP one (only that one should close on Esc).
 * Before it has registered (its first open render) it is not the top — Esc does nothing for that one frame.
 * ⓘ The snapshot is a BOOLEAN of this overlay, so a push / pop re-renders only the overlays whose answer changed.
 */
export const useOverlayStack = (opened: boolean): boolean => {
  const tokenRef = useRef<symbol>(Symbol("overlay"));
  const isTop = useSyncExternalStore(subscribe, () => opened && topOverlay() === tokenRef.current, () => false);

  useEffect(() => {
    if (!opened) {
      return undefined;
    }

    return pushOverlay(tokenRef.current);
  }, [opened]);

  return isTop;
};

// ── Esc inside an open POPOVER closes the popover, not the drawer under it (suggested task, 2026-10-03) ──────────────
//
// ⚠⚠ Mantine's drawer / modal closes on an Esc keydown at the WINDOW, in the capture phase — before the popover's own
//   handler ever sees it — and skips only a target marked `data-mantine-stop-propagation="true"` (its Menu items carry
//   that mark; Popover contents do not). So Esc in a rich-text toolbar popover (highlight colour, text colour, link,
//   image / media URL) closed the whole drawer around the editor.
//   The same window capture listener marks the target of an Esc pressed INSIDE an open popover-type dropdown for the
//   length of that one event. The drawer then ignores it, the popover still closes itself (its own handler is on the
//   dropdown), and `useModalShortcuts` honours the same mark.
// ⚠ A dropdown is matched by Mantine's popover class OR by any floating dropdown (`data-position` + a `-dropdown` class):
//   Mantine names the class after the component (Menu, Combobox, ColorInput, DateInput, the date pickers…), so
//   `.mantine-Popover-dropdown` alone missed a calendar or a colour picker inside a drawer.
/**
 * A2e #91 — a FULL-SCREEN surface that handles its own Esc (the image crop editor, opened from inside a form drawer).
 * Put this attribute on its root (focusable, focused on open) and an Esc pressed inside it is marked like a popover's.
 */
export const OWN_ESCAPE_ATTR = "data-em-own-escape";

const POPOVER_DROPDOWN = `.mantine-Popover-dropdown, [data-position][class*="-dropdown"], [${OWN_ESCAPE_ATTR}]`;
const STOP = "data-mantine-stop-propagation";

/** Whether an Esc keydown at this target belongs to an open popover (its focus is inside the dropdown). */
export const escapeBelongsToPopover = (target: EventTarget | null | undefined): boolean => {
  const element = target as { closest?: (selector: string) => unknown } | null | undefined;

  return !!element && typeof element.closest === "function" && !!element.closest(POPOVER_DROPDOWN);
};

/** Mark an Esc pressed inside a popover so the drawers / modals underneath ignore it (exported for the guard). */
export const markPopoverEscape = (event: { key?: string; target?: EventTarget | null }): boolean => {
  if (event.key !== "Escape" || !escapeBelongsToPopover(event.target)) {
    return false;
  }

  const element = event.target as unknown as { getAttribute: (n: string) => string | null; setAttribute: (n: string, v: string) => void; removeAttribute: (n: string) => void };

  if (element.getAttribute(STOP) === "true") {
    return true;
  }

  element.setAttribute(STOP, "true");
  // Only for this keypress: the mark is lifted once the event has been through every listener.
  setTimeout(() => element.removeAttribute(STOP), 0);

  return true;
};

/** The one window capture listener: the stack snapshot, then the popover mark. Exported for the guard. */
export const onWindowKeydownCapture = (event: { key?: string; target?: EventTarget | null }): void => {
  snapshotForKeydown();
  markPopoverEscape(event);
};

let keydownInstalled = false;

if (typeof window !== "undefined" && !keydownInstalled) {
  keydownInstalled = true;
  window.addEventListener("keydown", (event) => onWindowKeydownCapture(event), { capture: true });
}
